import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import dayjs from 'dayjs';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import { Button, Card, ConfirmationDialog, StatusBadge } from '@/components/UI';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type ExperimentResult = NonNullable<RouterOutputs['experiment']['results']['experiment']>;
type ExperimentLifecycle = NonNullable<ExperimentResult['lifecycle']>;
type ResearchWorkspace = NonNullable<ExperimentLifecycle['researchWorkspace']>;

type ResearchWorkspaceCardProps = {
    experimentId: number;
    workspace: ResearchWorkspace | null;
    onChanged: () => void;
};

function workspaceStatusLabel(status: ResearchWorkspace['status']): string {
    switch (status) {
        case 'awaitingReadiness':
            return 'Preparing';
        case 'open':
            return 'Research open';
        case 'sealPending':
            return 'Closing';
        case 'sealed':
            return 'Sealed';
        case 'reclaiming':
            return 'Cleaning up';
        case 'reclaimed':
            return 'Sealed';
        case 'failed':
            return 'Unavailable';
    }
}

function workspaceDescription(workspace: ResearchWorkspace): string {
    if (workspace.status === 'awaitingReadiness') {
        return 'The research window opens after the experiment and all required feature data are ready.';
    }
    if (workspace.status === 'open') {
        return 'Parameter discovery, scorecard generation, interaction discovery, and screener optimization can start during this window.';
    }
    if (workspace.status === 'sealPending') {
        return 'No new feature-dependent work can start. Active work is receiving its bounded time to finish before cleanup.';
    }
    if (workspace.status === 'failed') {
        return 'The research workspace could not become ready. Completed durable experiment results remain available.';
    }
    return 'Large research inputs are no longer available. Completed discovery results, baselines, scorecards, screeners, and comparisons remain usable.';
}

export function ResearchWorkspaceCard({ experimentId, workspace, onChanged }: ResearchWorkspaceCardProps) {
    const [extensionReason, setExtensionReason] = useState('Continue scorecard and screener research');
    const [showSealConfirmation, setShowSealConfirmation] = useState(false);

    const extendMutation = useMutation(
        trpc.experiment.extendResearchWorkspace.mutationOptions({
            onSuccess: () => {
                showToast('Research window extended by 72 hours.', 'success');
                onChanged();
            },
            onError: (error) => showToast(error.message, 'error')
        })
    );
    const sealMutation = useMutation(
        trpc.experiment.sealResearchWorkspace.mutationOptions({
            onSuccess: (response) => {
                setShowSealConfirmation(false);
                if (response.sealBlocked) {
                    showToast('Seal requested. Active work will finish before cleanup.', 'warning');
                } else {
                    showToast('Research workspace sealed.', 'success');
                }
                onChanged();
            },
            onError: (error) => showToast(error.message, 'error')
        })
    );

    if (!workspace) return null;

    const expiresAt = workspace.researchExpiresAtUnix ? dayjs.unix(workspace.researchExpiresAtUnix) : null;
    const canExtend = workspace.extensionAvailable && extensionReason.trim().length > 0;

    return (
        <>
            <Card className="bg-zinc-900 mb-6 border border-zinc-800">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <h2 className="text-base font-semibold text-white">Research workspace</h2>
                            <StatusBadge status={workspace.status}>{workspaceStatusLabel(workspace.status)}</StatusBadge>
                        </div>
                        <p className="max-w-3xl text-sm text-zinc-300">{workspaceDescription(workspace)}</p>
                        {expiresAt && workspace.status === 'open' && (
                            <p className="mt-2 text-sm text-amber-300">
                                New research closes {expiresAt.format('MMM D, YYYY h:mm A')} ({expiresAt.fromNow()}).
                            </p>
                        )}
                        {!workspace.featureDependentOperationsAvailable && workspace.status !== 'awaitingReadiness' && (
                            <p className="mt-2 text-xs text-zinc-500">
                                Clone or rerun the experiment to start new feature-dependent research.
                            </p>
                        )}
                    </div>

                    {workspace.status === 'open' && (
                        <div className="w-full space-y-2 lg:w-[360px]">
                            {workspace.extensionAvailable ? (
                                <div className="flex gap-2">
                                    <input
                                        aria-label="Research extension reason"
                                        className="min-w-0 flex-1 rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                                        maxLength={255}
                                        value={extensionReason}
                                        onChange={(event) => setExtensionReason(event.target.value)}
                                    />
                                    <Button
                                        disabled={!canExtend || extendMutation.isPending}
                                        size="sm"
                                        variant="secondary"
                                        onClick={() =>
                                            extendMutation.mutate({
                                                experimentId,
                                                reason: extensionReason.trim()
                                            })
                                        }
                                    >
                                        Extend 72h
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-xs text-zinc-500">The one-time extension has been used.</p>
                            )}
                            <Button
                                className="w-full"
                                disabled={sealMutation.isPending}
                                size="sm"
                                variant="danger"
                                onClick={() => setShowSealConfirmation(true)}
                            >
                                Seal research workspace now
                            </Button>
                        </div>
                    )}
                </div>
            </Card>

            <ConfirmationDialog
                confirmText="Seal Workspace"
                confirmVariant="danger"
                isConfirmLoading={sealMutation.isPending}
                isOpen={showSealConfirmation}
                message="Sealing is irreversible. New parameter discovery, scorecard generation, interaction discovery, and screener optimization will require a new experiment. Completed outputs and baselines will remain usable."
                title="Seal research workspace?"
                onCancel={() => setShowSealConfirmation(false)}
                onConfirm={() =>
                    sealMutation.mutate({
                        experimentId,
                        confirmIrreversibleSeal: true
                    })
                }
            />
        </>
    );
}
